import React, { useMemo, useState } from 'react';
import { arrayOf, bool, func, node, oneOf, oneOfType, shape, string } from 'prop-types';

import InputRadioItem from './InputRadioItem';

export const normalizeValue = (val) => {
  if (val === undefined || val === null || val === '') return undefined;

  switch (typeof val) {
    case 'string':
      return {
        label: val,
        value: val,
      };
    case 'object':
    default:
      return val;
  }
};

const InputRadio = (props) => {
  const {
    id,
    name,
    className,
    defaultValue: rawDefaultValue,
    disabled,
    error,
    flexDirection,
    onChange,
    options: rawOptions,
    style,
    value: rawValue,
    ...inputProps
  } = props;

  const options = useMemo(() => rawOptions.map((opt) => normalizeValue(opt)), [rawOptions]);
  const optionsType = useMemo(() => typeof (rawOptions?.[0] || ''), [rawOptions]);
  const defaultValue = useMemo(() => normalizeValue(rawDefaultValue), [rawDefaultValue]);
  const propsValue = useMemo(() => normalizeValue(rawValue), [rawValue]);

  // Store value on state when component is uncontrolled
  const isControlled = useMemo(() => propsValue !== undefined, [propsValue]);
  const [stateValue, setStateValue] = useState(defaultValue);

  const handleChange = (opt) => {
    const event = { ...new Event('change') };
    const newValue = options.find((v) => v.value === opt.value);
    switch (optionsType) {
      case 'object':
        event.target = { value: newValue, checked: true };
        break;
      case 'string':
      default:
        event.target = { value: newValue.value, checked: true };
        break;
    }
    onChange(event);

    if (!isControlled) setStateValue(newValue);
  };

  return (
    <div style={{ display: 'flex', flexDirection, ...style }}>
      {options.map((opt, i) => (
        <InputRadioItem
          key={opt.value}
          id={`${id}-${i}`}
          name={name}
          value={opt.value}
          className={className}
          style={{
            ...(flexDirection === 'row' ? { marginRight: 8 } : {}),
            ...(flexDirection === 'column' ? { marginBottom: 8 } : {}),
          }}
          // Component is always controlled
          // If value is provided, use the provided value. Otherwise, use value stored on state
          checked={isControlled ? propsValue?.value === opt.value : stateValue?.value === opt.value}
          // Do not pass defaultValue
          onChange={() => handleChange(opt)}
          disabled={disabled}
          {...inputProps}
        >
          {opt.label}
        </InputRadioItem>
      ))}
    </div>
  );
};

const optionPropType = oneOfType([
  string,
  shape({ value: string, label: oneOfType([string, node]) }),
]);

InputRadio.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  className: string,
  defaultValue: optionPropType,
  disabled: bool,
  error: string,
  flexDirection: oneOf(['row', 'column']),
  onChange: func,
  options: arrayOf(optionPropType),
  style: shape({}),
  value: optionPropType,
};

InputRadio.defaultProps = {
  className: '',
  defaultValue: '',
  disabled: false,
  error: '',
  flexDirection: 'column',
  onChange: () => {},
  options: [],
  style: {},
  value: undefined,
};

export default InputRadio;
